import moment from 'moment'
import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import ActivateTable from '@/components/pages/activate/table/table-tmpl'
import ActivateSearchbar from '@/components/pages/activate/activate-searchbar/activate-searchbar-tmpl'
import OrderLicenseForm from '@/components/pages/activate/order-license-form/order-license-form-tmpl'
import { filter, sort } from '../table/table-functions'

const LICENSETYPES = {
  KIOSK: 'KIOSK',
  SYSTEMS: 'SYSTEMS',
  WIDGETS: 'WIDGETS'
}

const getLicenseAccToType = (licenses, type) => {
  return licenses.reduce((license, code) => {
    if (code.type === type) {
      license.push({
        name: code.name,
        code: code.activationCode
      })
    }
    return license
  }, [])
}

export default {
  name: 'license-ordering',
  inject: [
    '$utils',
    '$api',
    '$apiError'
  ],
  components: {
    ActivateTable,
    ActivateSearchbar,
    OrderLicenseForm
  },
  props: {},
  watch: {
    dataProp (val) {
      this.dataPropVal = val
    }
  },
  mixins: [AuthMixin],
  data () {
    return {
      searchVal: null,
      isSearchResultEmpty: false,
      isTableEmpty: false,
      // For Activate Table
      activateTableColumns: [
        {
          key: 'id',
          label: this.$t('licenseOrdering.orderNumber'),
          type: 'text',
          thStylingClasses: ['text-left'],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'creationDate',
          label: this.$t('licenseOrdering.orderDate'),
          type: 'text',
          sortable: true,
          thStylingClasses: ['text-left', 'pr-4', 'order-date'],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'customer',
          label: this.$t('licenseOrdering.comment1'),
          type: 'input',
          sortable: true,
          placeholder: 'Customer',
          thStylingClasses: ['text-left'],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'po',
          label: this.$t('licenseOrdering.comment2'),
          type: 'input',
          sortable: true,
          placeholder: 'PO',
          thStylingClasses: ['text-left'],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'widgets',
          label: LICENSETYPES.WIDGETS,
          type: 'expandable',
          thStylingClasses: ['text-[#034B8A]', 'text-right'],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'kiosk',
          label: LICENSETYPES.KIOSK,
          type: 'expandable',
          thStylingClasses: ['text-[#1B998B]', 'text-right'],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'systems',
          label: LICENSETYPES.SYSTEMS,
          type: 'expandable',
          thStylingClasses: ['text-[#ED1B57]', 'text-right'],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'expanded',
          label: '',
          type: 'expand',
          thStylingClasses: [],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        },
        {
          key: 'status',
          label: '',
          type: 'action',
          thStylingClasses: [],
          tdStylingClasses: ['align-top', 'border-b-[#EBECF0]'],
          tdValueStylingClasses: ['pt-[7px]']
        }
      ],
      activateTableData: [],
      filteredTableData: [],
      sortData: {
        columnKey: null,
        order: null
      },
      isCommitingLicense: false
    }
  },
  methods: {
    deleteOrder (index) {
      const selectedRow = this.filteredTableData[index]

      const deleteOrderRequest = () => {
        this.$utils.mainLoader(true)
        this.$api.delete(`${CONFIG.API_BASE_URL}/licenses/${selectedRow.orderId}`)
          .then(() => {
            this.getOrders()
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { deleteOrderRequest() }, showDialogNotification: true })
          })
      }

      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        title: this.$t('confirm.cancelOrder'),
        description: this.$t('licenseOrdering.cancelOrderWarning'),
        confirmButtonText: this.$t('buttons.cancelOrder'),
        callbacks: {
          eventOKButtonClick: () => deleteOrderRequest()
        }
      })
    },
    updateComment (data) {
      if (this.isCommitingLicense) {
        return
      }

      const selectedRow = this.filteredTableData[data.index]
      const requestData = {
        [data.columnKey]: data.value
      }

      this.isCommitingLicense = true
      this.$api.put(`${CONFIG.API_BASE_URL}/licenses/${selectedRow.orderId}`, requestData)
        .then(() => {
          this.getOrders()
        })
        .catch((error) => {
          this.$utils.mainLoader(false)
          this.$apiError.handleAPIRequestError(error, { originalRequest: () => { this.updateComment() }, showDialogNotification: true })
        })
        .finally(() => {
          this.isCommitingLicense = false
        })
    },
    getLicenseCodes (index) {
      const selectedOrder = this.filteredTableData[index]

      if (selectedOrder.expanded) {
        selectedOrder.expanded = false
      } else {
        this.$utils.mainLoader(true)
        this.$api.get(`${CONFIG.API_BASE_URL}/licenses/${selectedOrder.orderId}`)
          .then(response => {
            selectedOrder.expanded = true

            const kioskCodes = getLicenseAccToType(response.data.licenses, LICENSETYPES.KIOSK)
            const widgetsCodes = getLicenseAccToType(response.data.licenses, LICENSETYPES.WIDGETS)
            const systemsCodes = getLicenseAccToType(response.data.licenses, LICENSETYPES.SYSTEMS)

            if (widgetsCodes.length) {
              selectedOrder.widgets = {
                ...selectedOrder.widgets,
                licenseCode: widgetsCodes
              }
            }

            if (kioskCodes.length) {
              selectedOrder.kiosk = {
                ...selectedOrder.kiosk,
                licenseCode: kioskCodes
              }
            }

            if (systemsCodes.length) {
              selectedOrder.systems = {
                ...selectedOrder.systems,
                licenseCode: systemsCodes
              }
            }

            this.$utils.mainLoader(false)
          })
      }

      this.filteredTableData[index] = selectedOrder
    },
    formatOrdersNew (orders) {
      const formattedOrders = orders.map((order) => {
        const { orderId, creationDate } = order

        const kioskOrders = order.summary.filter((license) => license.type === LICENSETYPES.KIOSK)
        const widgetsOrders = order.summary.filter((license) => license.type === LICENSETYPES.WIDGETS)
        const systemsOrders = order.summary.filter((license) => license.type === LICENSETYPES.SYSTEMS)

        const newOrder = {
          id: `#${orderId}`,
          orderId,
          creationDate: moment(creationDate).format('DD MMM YYYY'),
          rawCreationDate: creationDate,
          customer: order?.customer,
          po: order?.po,
          widgets: widgetsOrders.length
            ? { count: widgetsOrders[0].amount }
            : null,
          kiosk: kioskOrders.length
            ? { count: kioskOrders[0].amount }
            : null,
          systems: systemsOrders.length
            ? { count: systemsOrders[0].amount }
            : null,
          expanded: false,
          status: order.activated ? 'activated' : 'submitted'
        }

        return newOrder
      })

      return [...formattedOrders]
    },
    getOrders () {
      const getOrdersRequest = () => {
        this.$utils.mainLoader(true)
        this.$api.get(`${CONFIG.API_BASE_URL}/licenses`)
          .then(response => {
            const orders = response.data?.orders ?? []

            this.activateTableData = this.formatOrdersNew(orders)
            this.isTableEmpty = this.activateTableData.length < 1
            this.filteredTableData = this.filterOrdersToShow(this.activateTableData)
            this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { getOrdersRequest() }, showDialogNotification: true })
          })
      }
      getOrdersRequest()
    },
    filterOrdersToShow (orders) {
      const searchableFields = ['orderId', 'creationDate', 'customer', 'po']
      const { columnKey, order } = this.sortData

      let filteredOrders = filter(orders, searchableFields, this.searchVal)

      if (columnKey && order) {
        filteredOrders = sort(filteredOrders, columnKey, order)
      }

      return filteredOrders
    },
    handleSubmitOrder (buyOrderForm) {
      const apiRequest = () => {
        const requestData = { licenses: [] }

        if (buyOrderForm.numberOfWidgets && buyOrderForm.numberOfWidgets !== 0 && !isNaN(buyOrderForm.numberOfWidgets)) {
          requestData.licenses.push({
            type: LICENSETYPES.WIDGETS,
            amount: buyOrderForm.numberOfWidgets
          })
        }

        if (buyOrderForm.numberOfKiosk && buyOrderForm.numberOfKiosk !== 0 && !isNaN(buyOrderForm.numberOfKiosk)) {
          requestData.licenses.push({
            type: LICENSETYPES.KIOSK,
            amount: buyOrderForm.numberOfKiosk
          })
        }

        if (buyOrderForm.numberOfSystem && buyOrderForm.numberOfSystem !== 0 && !isNaN(buyOrderForm.numberOfSystem)) {
          requestData.licenses.push({
            type: LICENSETYPES.SYSTEMS,
            amount: buyOrderForm.numberOfSystem
          })
        }

        // Validate input values, value should be a number
        const invalidInput = []
        if (buyOrderForm) {
          if (buyOrderForm.numberOfKiosk && isNaN(buyOrderForm.numberOfKiosk)) invalidInput.push({ numberOfKiosk: buyOrderForm.numberOfKiosk })
          if (buyOrderForm.numberOfSystem && isNaN(buyOrderForm.numberOfSystem)) invalidInput.push({ numberOfSystem: buyOrderForm.numberOfSystem })
          if (buyOrderForm.numberOfWidgets && isNaN(buyOrderForm.numberOfWidgets)) invalidInput.push({ numberOfWidgets: buyOrderForm.numberOfWidgets })
        }

        if (invalidInput.length) {
          EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
            type: 'error',
            alert: {
              message: this.$t('label.invalidDSOSTypeAmount')
            }
          })
          return
        }

        if (!requestData.licenses.length) return

        this.$utils.mainLoader(true)
        this.$api.post(`${CONFIG.API_BASE_URL}/licenses`, requestData)
          .then(response => {
            this.getOrders()
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
          })
      }
      apiRequest()
    },
    handleSearchBarChange (keyword) {
      this.searchVal = keyword
      this.filteredTableData = this.filterOrdersToShow(this.activateTableData)
      this.isSearchResultEmpty = this.filteredTableData.length < 1
    },
    handleColumnSort (data) {
      let columnKey = data.columnKey

      if (columnKey === 'creationDate') {
        columnKey = 'rawCreationDate'
      }

      this.sortData = {
        columnKey,
        order: data.order
      }

      if (!data.order) {
        this.filteredTableData = this.filterOrdersToShow(this.activateTableData)
      }

      this.filteredTableData = this.filterOrdersToShow(this.filteredTableData)
    }
  },
  mounted () {
    this.isAuthenticated({
      callback: () => {
        this.getOrders()
      }
    })
  }
}
